import {BelegDTO, GeschaeftskundendatenDTO, KundendatenDTO, PrivatkundendatenDTO} from '../openapi/fakturierung-openapi';
import {Belegart} from './belegart.enum';


export function isGeschaeftskundendaten(obj: KundendatenDTO): obj is GeschaeftskundendatenDTO {
  return (obj as GeschaeftskundendatenDTO).typ == 'Geschaeftskundendaten';
}

export function isPrivatkundendaten(obj: KundendatenDTO): obj is PrivatkundendatenDTO {
  return (obj as PrivatkundendatenDTO).typ == 'Privatkundendaten';
}

export function isRechnung(belegart?: string | Belegart): belegart is string {
  if (!belegart) return false;
  return belegart?.toLowerCase() === Belegart.Rechnung.toLowerCase();
}

export function isGutschrift(belegart?: string | Belegart): belegart is string {
  if (!belegart) return false;
  return belegart?.toLowerCase() === Belegart.Gutschrift.toLowerCase();
}

export function isLieferschein(belegart?: string | Belegart): belegart is string {
  if (!belegart) return false;
  return belegart?.toLowerCase() === Belegart.Lieferschein.toLowerCase();
}

export function isAngebot(belegart?: string | Belegart): belegart is string {
  if (!belegart) return false;
  return belegart?.toLowerCase() === Belegart.Angebot.toLowerCase();
}

/** Nur Rechnung und Gutschrift werden als E-Rechnung ausgestellt. */
export function istERechnung(belegart?: string | Belegart): boolean {
  return isRechnung(belegart) || isGutschrift(belegart);
}

export function isBelegart(value?: string): value is Belegart {
  if (!value) return false;
  return Object.values(Belegart).some(belegart => belegart.toLowerCase() === value.toLowerCase());
}

/** Zielbelegarten, in die eine Belegart per Konvertierung uebergeben werden kann. */
export const ERLAUBTE_KONVERTIERUNGEN: Partial<Record<Belegart, Belegart[]>> = {
  [Belegart.Lieferschein]: [Belegart.Rechnung],
  [Belegart.Angebot]: [Belegart.Lieferschein, Belegart.Rechnung],
};

/**
 * Ein Beleg kann in die Zielbelegart uebergeben werden, sobald er festgeschrieben ist,
 * keinen aktiven Zielbeleg hat und das Paar in {@link ERLAUBTE_KONVERTIERUNGEN} steht.
 */
export function canKonvertieren(beleg: BelegDTO | undefined, ziel: Belegart): boolean {
  return konvertierungsZiele(beleg).includes(ziel);
}

/** Alle Zielbelegarten, in die der Beleg aktuell uebergeben werden kann; leer fuer `undefined`. */
export function konvertierungsZiele(beleg?: BelegDTO): Belegart[] {
  if (!beleg || !beleg.status.festgeschrieben || beleg.ziel) {
    return [];
  }
  return ERLAUBTE_KONVERTIERUNGEN[beleg.belegart.toUpperCase() as Belegart] ?? [];
}

/**
 * Das Stornieren erzeugt nur bei Rechnung und Gutschrift einen Korrekturbeleg;
 * bei Lieferschein und Angebot ist es ein reiner Statuswechsel.
 */
export function stornoErzeugtKorrekturbeleg(beleg?: BelegDTO): boolean {
  return !!beleg
    && (isRechnung(beleg.belegart) || isGutschrift(beleg.belegart));
}

/**
 * Ein Beleg kann storniert werden, solange er festgeschrieben und nicht bereits
 * storniert ist. Ein Beleg mit aktivem Zielbeleg ist ausgenommen.
 */
export function canStorno(beleg?: BelegDTO): boolean {
  return !!beleg
    && !!beleg.status.festgeschrieben
    && !beleg.status.storniert
    && !beleg.ziel;
}

/** Die B2G-Referenzfelder gibt es nur bei der Rechnung an einen Geschäftskontakt. */
export function hatReferenzfelder(belegart?: string | Belegart, kundendaten?: KundendatenDTO): boolean {
  return isRechnung(belegart) && !!kundendaten && isGeschaeftskundendaten(kundendaten);
}
